import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../api/supabase';

export default function AuthCallback() {
    const navigate = useNavigate();
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('success');
    const [showAlert, setShowAlert] = useState(false);
    const [status, setStatus] = useState('Processing your sign-in...');
    
    // Use ref to prevent double execution
    const hasRun = useRef(false);

    const handleAuthCallback = async () => {
        // Prevent double execution
        if (hasRun.current) {
            console.log('Already processing, skipping duplicate call');
            return;
        }
        hasRun.current = true;

        try {
            setStatus('Verifying your account...');
            
            // Get the session from Supabase
            const { data: { session }, error } = await supabase.auth.getSession();

            if (error) throw error;

            if (session) {
                const user = session.user;
                // Get stored form data
                const storedData = sessionStorage.getItem('pendingSubscription');
                const formData = storedData ? JSON.parse(storedData) : null;

                if (!formData) {
                    throw new Error('No subscription data found. Please start again.');
                }

                setStatus('Creating your account...');

                // Extract user info from Google
                const userMetadata = user.user_metadata;
                const email = user.email;
                const fullName = userMetadata.full_name || userMetadata.name || '';
                const [firstName = '', ...lastNameParts] = fullName.split(' ');
                const lastName = lastNameParts.join(' ') || '';

                // Prepare data for backend
                const phonNumberCompany = formData.companyPhone ? `${formData.companyPhoneCode}${formData.companyPhone}` : "";
                const subscriptionData = {
                    company: {
                        company_name: formData.companyName,
                        employee_count: formData.companySize,
                        industry: formData.industry,
                        overtime_rate_multiplier: 1.5,
                        over_time: "Daily",
                        minutes_before_clock_in_shift: "60",
                        business_phone_number: phonNumberCompany || null,
                        business_phone_code: formData.companyPhoneCode || null,
                    },
                    user: {
                        full_name: fullName,
                        first_name: firstName,
                        last_name: lastName,
                        email: email,
                        phone_number: userMetadata.phoneNumber || null,
                        phone_number_code: userMetadata.phoneNumberCode||null,
                        user_type: "Company Admin",
                        auth_provider: 'google',
                        supabase_user_id: user.id,
                    },
                    address: {},
                    plan: {
                        id: formData.planId || null,
                        billing_cycle: formData.billingCycle || null
                    },
                    isOAuthUser: true, // Add this flag!
                };

                console.log('Sending subscription data to backend...');

                // Call backend endpoint
                const API_URL = process.env.REACT_APP_BASE_URL;
                const response = await fetch(`${API_URL}/subscribe_company/oauth`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'appname': 'LimeTi',
                        'Authorization': `Bearer ${session.access_token}`
                    },
                    body: JSON.stringify(subscriptionData)
                });

                const result = await response.json();

                if (response.ok && result.success) {
                    setStatus('Success! Redirecting to login...');
                    sessionStorage.removeItem('pendingSubscription');
                    
                    handleAlert(
                        'Account created successfully! Redirecting to login...',
                        'success'
                    );

                    setTimeout(() => {
                        window.open(`${process.env.REACT_APP_DASHBOARD_URL}/login`, '_blank', 'noopener,noreferrer');
                        navigate('/request-demo')
                    }, 2000);

                } else {
                    handleAlert(result.message || 'Failed to create subscription. Please try again.', 'danger');
                    throw new Error(result.message || 'Failed to create account');
                }
            } else {
                throw new Error('No session found');
            }
        } catch (error) {
            console.error('Auth callback error:', error);
            setStatus(`Error: ${error.message}`);
            handleAlert(error.message || 'An error occurred', 'danger');
            
            setTimeout(() => {
                navigate('/request-demo?error=' + encodeURIComponent(error.message));
            }, 3000);
        }
    };

    const handleAlert = (message, type) => {
        setAlertMessage(message);
        setAlertType(type);
        setShowAlert(true);

        // Auto-hide success alerts after 5 seconds
        if (type === 'success') {
            setTimeout(() => {
                setShowAlert(false);
            }, 5000);
        }
    };

    useEffect(() => {
        const storedData = sessionStorage.getItem('pendingSubscription');
        if (storedData) {
            handleAuthCallback();
        } else {
            navigate('/request-demo?error=No subscription data found');
        }
    }, []); // Empty dependency array - runs only once

    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            flexDirection: 'column',
            gap: '20px',
            padding: '20px'
        }}>
            {/* Show the alert */}
            {showAlert && (
                <div
                    className={`alert alert-${alertType} alert-dismissible fade show mt-2`}
                    role="alert"
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: '50%',
                        right: 0,
                        zIndex: 9999,
                        margin: '0',   
                        padding: '1rem', 
                        transform: 'translateX(-50%)',
                        width: '80%',
                        maxWidth: '600px',
                    }}
                >
                    {alertMessage}
                    <button type="button" className="btn-close" onClick={() => setShowAlert(false)}></button>
                </div>
            )}

            <div className="spinner-border" style={{ color: '#7bb321' }}>
                <span className="sr-only"></span>
            </div>
            <p style={{ 
                fontSize: '16px', 
                color: '#666',
                textAlign: 'center',
                maxWidth: '400px'
            }}>
                {status}
            </p>
        </div>
    );
}